import { useMemo } from "react";

import { Column, createDataColumn } from "@/components/ui/table-final-saviour/Table";
import { ExpandedContent } from "@/components/ui/table-final-saviour/Table/TableExpandable";
import { primaryActiveRoleSelector, useAppSelector } from "@/store";
import { fixedNumberPrecision } from "@/utils/number.utils";
import { notNil } from "@/utils/validation.utils";

type DataRow = {
    "Expenses Event": string | null;
    "Expenses Name": string | null;
    "Expenses Total": number | null;
    "Expenses Cost"?: number | null;
    "Expenses Revenue"?: number | null;
};

type Props = {
    data: DataRow[];
    hideFinancialColumns?: boolean;
};

export const ExpensesSection = (props: Props) => {
    const { data, hideFinancialColumns } = props;

    const primaryActiveRole = useAppSelector(primaryActiveRoleSelector);
    const isServiceManager = primaryActiveRole?.name === "Service Manager";

    const showFinancialColumns = isServiceManager && hideFinancialColumns !== true;

    const columns: Column<DataRow>[] = useMemo(
        () => [
            createDataColumn({
                title: "Name",
                dataKey: "Expenses Name",
                dataType: "string",
            }),
            createDataColumn({
                title: "Total",
                dataKey: "Expenses Total",
                dataType: "number",
                align: "right",
                width: 140,
            }),
            ...(showFinancialColumns
                ? [
                      createDataColumn<DataRow>({
                          title: "Revenue",
                          dataKey: "Expenses Revenue",
                          dataType: "number",
                          align: "right",
                          width: 140,
                          getCellValue: ({ row: { "Expenses Revenue": value } }) =>
                              notNil(value) ? fixedNumberPrecision(value) : value,
                      }),
                      createDataColumn<DataRow>({
                          title: "Cost",
                          dataKey: "Expenses Cost",
                          dataType: "number",
                          align: "right",
                          width: 140,
                          getCellValue: ({ row: { "Expenses Cost": value } }) =>
                              notNil(value) ? fixedNumberPrecision(value) : value,
                      }),
                  ]
                : []),
            createDataColumn({
                title: "Event",
                dataKey: "Expenses Event",
                dataType: "number",
                width: 200,
            }),
        ],
        [showFinancialColumns],
    );

    if (data.length === 0) {
        return null;
    }

    return (
        <ExpandedContent.Section>
            <ExpandedContent.Section.Content>
                <ExpandedContent.Section.Content.Title>Expenses</ExpandedContent.Section.Content.Title>
            </ExpandedContent.Section.Content>

            <ExpandedContent.Section.Table<DataRow>
                dataSource={{ isFetching: false, isLoading: false, data }}
                columns={columns}
                maxHeight={400}
            />
        </ExpandedContent.Section>
    );
};
